import { sum, round } from 'lodash';
import { BuildTypeCode } from '../../../projects/project';
import {
  HeatPumpUnitFormModel,
  MicrogenerationCertificationSchemeLocationFormModel,
} from '../GetDataForQuoteOrDesignForm';
import { SwimmingPoolTypeCode } from '../quoteOrDesignFormData';
import { groundCollectorLengthLookupsByHeatPumpUnitName } from './groundCollectorLengthLookups';
import { HeatPumpFormModel } from './heatPumpFormModel';

export const calculateAdjustedHeatLossRateInWattsPerSquareMetre = (
  heatLossRateInWattsPerSquareMetre: string,
  location: MicrogenerationCertificationSchemeLocationFormModel | null,
): number | undefined => {
  const numericHeatLossRateInWattsPerSquareMetre = Number(heatLossRateInWattsPerSquareMetre);

  if (!location || isNaN(numericHeatLossRateInWattsPerSquareMetre)) {
    return undefined;
  }

  const result = (numericHeatLossRateInWattsPerSquareMetre * (20 - location.bivalentPointInDegreesCelsius)) / 23;

  return Math.round(result);
};

export const calculateBuildingHeatLoadInKilowatts = (
  adjustedHeatLossRateInWattsPerSquareMetre: number,
  areaInMetresSquared: string,
  swimmingPoolTypeCode: SwimmingPoolTypeCode,
  swimmingPoolPowerInKilowatts: string,
): number | undefined => {
  const numericAreaInMetresSquared = Number(areaInMetresSquared);

  if (isNaN(numericAreaInMetresSquared)) {
    return undefined;
  }

  let numericSwimmingPoolPowerInKilowatts = Number(swimmingPoolPowerInKilowatts);
  if (isNaN(numericSwimmingPoolPowerInKilowatts) || swimmingPoolTypeCode === 'None') {
    numericSwimmingPoolPowerInKilowatts = 0;
  }

  const result =
    (adjustedHeatLossRateInWattsPerSquareMetre * numericAreaInMetresSquared) / 1000 +
    numericSwimmingPoolPowerInKilowatts;

  return round(result, 1);
};

export const getOutputInKilowattsOfHeatPumpUnitForQuote = (
  heatPumpUnit: HeatPumpUnitFormModel,
  closestMicrogenerationCertificationSchemeLocation: MicrogenerationCertificationSchemeLocationFormModel | null,
  buildType: BuildTypeCode,
): number => {
  if (closestMicrogenerationCertificationSchemeLocation == null) {
    return 0;
  }

  const flowTemperature = buildType === 'NewBuild' ? 45 : 50;
  const outsideTemperature = closestMicrogenerationCertificationSchemeLocation.bivalentPointInDegreesCelsius;

  return getOutputInKilowattsOfHeatPumpUnit(heatPumpUnit, flowTemperature, outsideTemperature);
};

export const getOutputInKilowattsOfHeatPumpUnit = (
  heatPumpUnit: HeatPumpUnitFormModel,
  flowTemperature: number,
  outsideTemperature: number,
): number => {
  const roundedOutsideTemperature = Math.round(outsideTemperature);
  const outputRow = heatPumpUnit.outputsAtGivenTemperatures.find(
    row =>
      row.flowTemperatureInDegreesCelsius === flowTemperature &&
      row.outsideTemperatureInDegreesCelsius === roundedOutsideTemperature,
  );

  return outputRow ? outputRow.outputInKilowatts : 0;
};

export const percentageCoverageOfHeatPumpUnits = (
  buildingHeatLoadInKilowatts: number | null,
  heatPumpUnitParts: Array<HeatPumpUnitFormModel | null>,
  closestMicrogenerationCertificationSchemeLocation: MicrogenerationCertificationSchemeLocationFormModel | null,
  buildType: BuildTypeCode,
): number | null => {
  if (
    buildingHeatLoadInKilowatts == null ||
    isNaN(buildingHeatLoadInKilowatts) ||
    buildingHeatLoadInKilowatts === 0 ||
    closestMicrogenerationCertificationSchemeLocation == null ||
    heatPumpUnitParts.every(heatPumpUnit => heatPumpUnit == null)
  ) {
    return null;
  }
  const totalOutputInKilowatts = calculateTotalOutputInKilowatts(
    heatPumpUnitParts,
    closestMicrogenerationCertificationSchemeLocation,
    buildType,
  );
  const coverage = totalOutputInKilowatts / buildingHeatLoadInKilowatts;
  return Math.round(coverage * 100);
};

export const calculateTotalOutputInKilowatts = (
  heatPumpUnitParts: Array<HeatPumpUnitFormModel | null>,
  closestMicrogenerationCertificationSchemeLocation: MicrogenerationCertificationSchemeLocationFormModel,
  buildType: BuildTypeCode,
) => {
  return sum(
    heatPumpUnitParts.map(heatPumpUnit =>
      heatPumpUnit == null
        ? 0
        : getOutputInKilowattsOfHeatPumpUnitForQuote(
            heatPumpUnit,
            closestMicrogenerationCertificationSchemeLocation,
            buildType,
          ),
    ),
  );
};

export const calculateAnnualHeatingRequirementInKilowattHours = (
  heatPump: HeatPumpFormModel,
  numberOfBedrooms: string,
): number | null => {
  const {
    heatLossRateInWattsPerSquareMetre,
    areaInMetresSquared,
    closestMicrogenerationCertificationSchemeLocation,
    swimmingPoolTypeCode,
    swimmingPoolEnergyConsumptionInKilowattHours,
  } = heatPump;

  const numericAreaInMetresSquared = Number(areaInMetresSquared);
  const numericHeatLossRateInWattsPerSquareMetre = Number(heatLossRateInWattsPerSquareMetre);
  const numericNumberOfBedrooms = Number(numberOfBedrooms);

  if (
    isNaN(numericAreaInMetresSquared) ||
    isNaN(numericHeatLossRateInWattsPerSquareMetre) ||
    isNaN(numericNumberOfBedrooms) ||
    closestMicrogenerationCertificationSchemeLocation == null
  ) {
    return null;
  }

  let numericSwimmingPoolEnergyConsumptionInKilowattHours = Number(swimmingPoolEnergyConsumptionInKilowattHours);
  if (isNaN(numericSwimmingPoolEnergyConsumptionInKilowattHours) || swimmingPoolTypeCode === 'None') {
    numericSwimmingPoolEnergyConsumptionInKilowattHours = 0;
  }

  const annualHeatingDemandInKilowattHours =
    0.001 *
    24 *
    (numericHeatLossRateInWattsPerSquareMetre /
      (20 - closestMicrogenerationCertificationSchemeLocation.bivalentPointInDegreesCelsius)) *
    numericAreaInMetresSquared *
    closestMicrogenerationCertificationSchemeLocation.heatingDegreeDays;

  const assumedDailyWaterUsageInLitres = (numericNumberOfBedrooms + 1) * 45;

  const annualHotWaterRequirementInKilowattHours = 0.4234 * 38 * assumedDailyWaterUsageInLitres;

  return Math.round(
    annualHeatingDemandInKilowattHours +
      annualHotWaterRequirementInKilowattHours +
      numericSwimmingPoolEnergyConsumptionInKilowattHours,
  );
};

export const calculateGroundCollectorLengthInMetres = (
  heatPumpUnits: Array<HeatPumpUnitFormModel>,
  heatingRequirementInKilowattHours: number,
): number | null => {
  if (heatPumpUnits.length !== 1 || heatingRequirementInKilowattHours > 150000) {
    return null;
  }

  const heatPumpUnitName = heatPumpUnits[0].displayName;

  const groundCollectorLengthLookupForHeatPumpUnit = groundCollectorLengthLookupsByHeatPumpUnitName[heatPumpUnitName];

  if (groundCollectorLengthLookupForHeatPumpUnit == null) {
    return null;
  }

  const matchingLookup = groundCollectorLengthLookupForHeatPumpUnit.find(
    lookup => lookup.heatingRequirementInKilowattHours >= heatingRequirementInKilowattHours,
  );

  return matchingLookup == null ? null : matchingLookup.groundCollectorLengthInMetres;
};
